/*
 * Copyright (c) 2022 Payoneer Germany GmbH. All rights reserved.
 */
var $ = require('jquery');
var rootScopeService = require('./root-scope.service');
var loading = require('./loading.service');
var translate = require('./translate.service');
var { constructQueryParams } = require('./util/common-utils');
var submitButtonService = require('./submit-button');
var popupModal = require('../../templates').popupModal;
var redirectInfoModalID = 'redirect-tab-info-modal';
var handleServiceError = require('./handle-error.service');

/**
 * Show popup modal when payment page is active in another tab/popup
 */
function showProviderRedirectInfoModal() {
    var redirectTabInfoModalClassName = 'op-payment-redirect-info-modal';

    if (loading.isLoadingState()) {
        loading.hideFullPageLoading();
        loading.hidePaymentLoading();
    }
    if (!$('#' + redirectInfoModalID).length) {
        rootScopeService.getMainContainer().append(
            popupModal({
                header:  translate('redirect.modal.text'),
                goToPaymentBtnLabel: translate('redirect.modal.button.label'),
                body: null,
                modalID: redirectInfoModalID,
                wrapperClasses: redirectTabInfoModalClassName + loading.getLoadingInContainerClass(),
            })
        );
    } else {
        $('#' + redirectInfoModalID).show();
    }
    submitButtonService.disableSubmitButton();
}

/**
 * Hide popup modal
 */
function hideProviderRedirectInfoModal() {
    if ($('#' + redirectInfoModalID).length) {
        $('#' + redirectInfoModalID).hide();
    }

    submitButtonService.enableSubmitButton();
}

/**
 * Keep an eye on controlled tab object and detect if controlled tab gets closed.
 *
 * @param {Window} tabRef Controlled window/tab reference.
 * @param {function(isClosed: bool)} callback - callback function to trigger on child tab close
 */
function controlledTabCloseEventListener(tabRef, callback) {
    if (!tabRef) {
        return;
    }

    var handle = setInterval(listener, 1000);
    function listener() {
        if (tabRef.closed) {
            callback(tabRef.closed);
            clearInterval(handle);
        }
    }
}

/**
 * Accept OPG response object and properly shape the object.
 *
 * @param {Object} data - URL query params sent by OPG backend or custom built opg response object.
 * @returns {Object} OPG response
 */
function formatOPGResponse(data) {
    if (typeof data !== 'object') {
        return handleServiceError('data must be an object');
    }

    if (!data.interactionCode || !data.interactionReason) {
        return handleServiceError('interactionCode and interactionReason is required.');
    }

    return {
        interaction: {
            code: data.interactionCode,
            reason: data.interactionReason,
        },
        resultInfo: data?.resultInfo,
        links: { redirect: null },
        redirect: {
            url: data?.referrer || null, // referrer is created and sent from child tab.
            method: 'GET',
            parameters: Object.entries({ ...data, postRedirect: true })
                .filter(([name]) => !['interaction', 'referrer'].includes(name))
                .map(([name, value]) => {
                    return { name, value };
                }),
            type: data.interactionCode === 'PROCEED' ? 'RETURN' : 'CANCEL',
        },
    };
}

/**
 * Open the given URL in new window/tab/popup according to given `specs`.
 * This function is also responsible to listen close event of controlled tab.
 *
 * @param {string} url URL to open in new tab
 * @param {boolean} suppress - If `true` open new window from top level window
 * @param {string} specs - optional third param for `window.open`
 * @param {function} callback - `operateCallback` from `operate.service.js`
 * @param {string} target target tab name. eg: _blank, _self or any custom name
 * @returns {Window} Window reference of the newly opened controlled window/tab
 */
function openInControlledWindow(url, suppress, specs = '', callback, target = 'op-redirect-tab') {
    /**
     * Note: We are not checking if child tab is opened already (`childTabRef || ! childTabRef.closed`)
     * to force reload child tab with supplied params each time
     */
    var childTabRef = suppress ? window.top.open(url, target, specs) : window.open(url, target, specs);

    /**
     * Handle unexpected close of Controlled tab.
     */
    controlledTabCloseEventListener(childTabRef, function (isClosed) {
        if (isClosed && !rootScopeService.isAlreadyNotifiedAboutCancelEvent()) {
            /**
             * Custom opg response object when end user cancels transaction either by closing controlled tab.
             */
            hideProviderRedirectInfoModal();
            if (typeof callback === 'function') {
                rootScopeService.setIsCancelEventFired(true);
                callback(
                    null,
                    formatOPGResponse({
                        interactionCode: 'ABORT',
                        interactionReason: 'SYSTEM_FAILURE',
                        resultInfo: 'Customer abort transaction operation which invalidates LIST',
                    })
                );
            }
        }
    });

    return childTabRef;
}

/**
 * Listen for button click events in parent window popup modal.
 *
 * @param {Window} - window reference of controlled tab
 */
function popupButtonsOnClick(winRef) {
    if (!winRef || (winRef && !winRef instanceof Window)) return;

    $(document).ready(function () {
        $('#go-to-payment').click(function () {
            winRef.focus();
        });
    });
}

/**
 * Proceed with redirect response. eg: trigger `onResult` callback
 *
 * @param {Object} event - Event object
 * @param {function} callback - `operateCallback` from `operate.service.js`
 */
function proceedWithRedirectResponse(event, callback) {
    /**
     * Exit If child domain is not same as parent domain.
     */
    if (event.origin !== window.location.origin) {
        return;
    }

    hideProviderRedirectInfoModal();
    if (typeof callback === 'function') {
        rootScopeService.setIsCancelEventFired(true);
        callback(null, formatOPGResponse(event.data));
    }
}

/**
 * Listen to messages from Controlled window/tab and trigger callback function.
 *
 * @param {function(event): void} callback - callback function
 */
function childTabMessagesListener(callback) {
    if (!callback || typeof callback !== 'function') {
        return handleServiceError('Callback function is missing or invalid');
    }

    window.addEventListener('message', callback, false);
}

/**
 * Handle redirect in new tab and keep track of controlled tab.
 *
 * @param data The data containing redirect info (eg: url, params)
 * @param controlledTabSpecs - Specs params of `Window.open`
 * @param {function} callback - `operateCallback` from `operate.service.js`
 */
function providerRedirect(data, controlledTabSpecs = '', callback) {
    var suppress = data.redirect.suppressIFrame !== undefined ? data.redirect.suppressIFrame : rootScopeService.getSuppressIFrameDefault();

    var winRef = openInControlledWindow(
        constructQueryParams(data.redirect?.url, data.redirect?.parameters),
        suppress,
        controlledTabSpecs,
        callback
    );

    if (!winRef || winRef.closed) return;

    // Open the popup info modal.
    // loading.showRedirectTabInfo();
    showProviderRedirectInfoModal();

    /**
     * Listen button clicks.
     */
    popupButtonsOnClick(winRef);

    /**
     * Do appropriate actions according to messages sent from controlled tab.
     */
    childTabMessagesListener((e) => proceedWithRedirectResponse(e, callback));
}

module.exports = {
    providerRedirect,
};
